//@ts-check
import { renderAllSections } from './js-utils-rendersection.js'
import {
  buildOrderedCartAddPayload,
  reorderCartByParentChild,
} from './js-utils-cart-parent-child.js'

/** @typedef {{ ok?: boolean; error?: string; variantId?: number; quantity?: number; properties?: Record<string, string> }} MessagesProxyResponse */

class CProductForm extends HTMLElement {
  constructor() {
    super()
    this.form = this.querySelector('form')
    const formId = this.form?.getAttribute('id')
    const panel = this.closest('c-panel')
    /** @type {HTMLButtonElement|null} */
    this.btnSubmit =
      (formId && panel?.querySelector(`button[form="${formId}"]`)) ??
      (formId ? document.querySelector(`button[form="${formId}"]`) : null)
    /** @type {HTMLButtonElement|null} */
    this.btnSubmitAlreadyInfos =
      formId?.startsWith('product-form-already-infos') && panel
        ? panel.querySelector(`button[form="${formId}"]`)
        : null
    this.type = this.dataset.type || null
    this.urlProduct = this.dataset.productUrl

    this.sectionBtnSticky = [
      ...document.querySelectorAll('.section-btn-sticky'),
    ]
    this.btnSticky = [...document.querySelectorAll('.product-btn-sticky')]

    this.allPanel = Array.from(this.querySelectorAll('c-panel'))
    this.sectionsRender = [
      {
        title: 'header',
        selector: ['[data-render-icon-cart]'],
      },
      {
        title: `${this.btnSticky[0]?.id}`,
        selector: ['[data-render-btn-sticky]'],
      },
      {
        title: `${this.btnSticky[1]?.id}`,
        selector: ['[data-render-btn-sticky]'],
      },
      {
        title: 'product',
        selector: [
          '[data-render-price]',
          '[data-render-quantity]',
          '[data-render-btn-add-product]',
          '[data-render-panel-contact]',
          '[data-render-panel-product]',
          '[data-render-card-option]',
        ],
      },
      {
        title: 'cart-drawer',
        selector: ['[data-render-total]', '[data-render-panel-cart]'],
      },
    ]
    this.sectionsRender2 = [
      {
        title: 'product',
        selector: [],
      },
    ]
  }

  connectedCallback() {
    this.form?.addEventListener('submit', (e) => {
      void this.handleFormSubmit(e)
    })
    this.form?.addEventListener('change', (e) => {
      const t = e.target
      if (
        t instanceof HTMLInputElement &&
        t.matches('[data-messages-csv]') &&
        t.closest('[data-types-bon-csv]')
      ) {
        const region = t.closest('[data-types-bon-csv]')
        if (region instanceof HTMLElement) this.clearCsvError(region)
      }
    })
  }

  /**
   * @param {HTMLElement|null} csvRegion
   */
  clearCsvError(csvRegion) {
    const el = csvRegion?.querySelector('[data-messages-csv-error]')
    if (!(el instanceof HTMLElement)) return
    el.textContent = ''
    el.classList.add('is-hidden')
    el.setAttribute('role', 'status')
  }

  /**
   * @param {HTMLElement} csvRegion
   * @param {string} message
   */
  setCsvError(csvRegion, message) {
    const el = csvRegion.querySelector('[data-messages-csv-error]')
    if (!(el instanceof HTMLElement)) return
    el.textContent = message
    el.classList.remove('is-hidden')
    el.setAttribute('role', 'alert')
  }

  /**
   * @param {Event} e
   */
  async handleFormSubmit(e) {
    if (!this.form) return
    e.preventDefault()
    if (this.btnSubmit) this.btnSubmit.setAttribute('disabled', 'true')
    if (this.btnSubmitAlreadyInfos)
      this.btnSubmitAlreadyInfos.setAttribute('disabled', 'true')

    // If CSV input exists
    if (this.form.querySelector('[data-messages-csv]')) {
      const csvHandled = await this.maybeAddViaMessagesCsv()
      if (csvHandled) return
    }

    this.syncQuantityBeforeSubmit()

    const orderedPayload = buildOrderedCartAddPayload(this.form)
    const hasOptions = (orderedPayload?.items.length ?? 0) > 1

    try {
      if (orderedPayload && hasOptions) {
        const addResponse = await fetch(
          window.Shopify.routes.root + 'cart/add.js',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(orderedPayload),
          }
        )
        await addResponse.json().catch(() => ({}))
        await reorderCartByParentChild().catch((error) => {
          console.error(error)
        })
      } else {
        const config = this.fetchConfig('javascript')
        config.headers['X-Requested-With'] = 'XMLHttpRequest'
        delete config.headers['Content-Type']
        config.body = new FormData(this.form)

        const addResponse = await fetch(
          window.Shopify.routes.root + 'cart/add.js',
          config
        )
        await addResponse.json().catch(() => ({}))
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      await this.runAfterCartAdd()
    }
  }

  restoreSubmitButtons() {
    if (this.btnSubmit) this.btnSubmit.removeAttribute('disabled')
    if (this.btnSubmitAlreadyInfos)
      this.btnSubmitAlreadyInfos.removeAttribute('disabled')
  }

  syncQuantityBeforeSubmit() {
    if (!this.form) return

    const variantIdInput =
      this.form.querySelector('input[name="id"]') ??
      this.form.querySelector('input[name="items[0][id]"]')
    if (!(variantIdInput instanceof HTMLInputElement)) return

    const quantity = this.resolveVariantQuantity(variantIdInput.value)
    const qtyInput =
      this.form.querySelector('[data-product-form-quantity]') ??
      this.form.querySelector('input[name="quantity"]') ??
      this.form.querySelector('input[name="items[0][quantity]"]')
    if (qtyInput instanceof HTMLInputElement) {
      qtyInput.value = String(quantity)
    }
  }

  /**
   * @param {string} variantId
   */
  resolveVariantQuantity(variantId) {
    const qtyByVariant = document.querySelector(
      `[data-input-quantity="${variantId}"]`
    )
    if (qtyByVariant instanceof HTMLInputElement) {
      const n = parseInt(qtyByVariant.value, 10)
      return Number.isFinite(n) && n > 0 ? n : 1
    }
    const qtyHidden = this.form?.querySelector('[data-product-form-quantity]')
    if (qtyHidden instanceof HTMLInputElement) {
      const n = parseInt(qtyHidden.value, 10)
      return Number.isFinite(n) && n > 0 ? n : 1
    }
    return 1
  }

  /**
   * B2B named + CSV: POST app proxy then cart/add.js with returned line item.
   *
   * @returns {Promise<boolean>} true if this path handled the submit (do not run default add)
   */
  async maybeAddViaMessagesCsv() {
    const fileInput = this.form?.querySelector('[data-messages-csv]')
    if (!(fileInput instanceof HTMLInputElement)) return false

    const csvRegion = fileInput.closest('[data-types-bon-csv]')
    if (!(csvRegion instanceof HTMLElement)) return false
    if (csvRegion.hidden || fileInput.disabled) return false

    this.clearCsvError(csvRegion)

    const file = fileInput.files?.[0]
    if (!file) {
      this.setCsvError(csvRegion, 'Veuillez sélectionner un fichier CSV.')
      this.restoreSubmitButtons()
      return true
    }

    const variantId = fileInput.dataset.variantId
    if (!variantId) {
      this.setCsvError(csvRegion, 'Identifiant de variante manquant.')
      this.restoreSubmitButtons()
      return true
    }

    const quantity = this.resolveVariantQuantity(variantId)
    const formData = new FormData()
    formData.append('csv', file)
    formData.append('variantId', variantId)
    formData.append('quantity', String(quantity))

    try {
      const res = await fetch('/apps/barriere/messages', {
        method: 'POST',
        body: formData,
      })
      /** @type {MessagesProxyResponse} */
      const data = await res.json()

      if (!data.ok) {
        const msg =
          typeof data.error === 'string' ? data.error : 'Erreur de validation'
        this.setCsvError(csvRegion, msg)
        this.restoreSubmitButtons()
        return true
      }

      const variantIdNum = data.variantId
      const qty = data.quantity
      const props = data.properties
      if (
        variantIdNum == null ||
        qty == null ||
        props == null ||
        typeof props !== 'object'
      ) {
        this.setCsvError(csvRegion, 'Réponse serveur invalide.')
        this.restoreSubmitButtons()
        return true
      }

      const lineProperties = { ...props, _type_bon: 'named' }

      const addRes = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantIdNum,
              quantity: qty,
              properties: lineProperties,
            },
          ],
        }),
      })

      if (!addRes.ok) {
        const err = await addRes.json().catch(() => ({}))
        let desc = "Impossible d'ajouter au panier"
        if (err && typeof err === 'object' && 'description' in err) {
          const d = /** @type {{ description?: unknown }} */ (err).description
          if (typeof d === 'string' && d) desc = d
        }
        this.setCsvError(csvRegion, desc)
        this.restoreSubmitButtons()
        return true
      }

      this.clearCsvError(csvRegion)
      await this.runAfterCartAdd()
      return true
    } catch (err) {
      console.error(err)
      this.setCsvError(
        csvRegion,
        'Une erreur est survenue. Vérifiez le fichier et réessayez.'
      )
      this.restoreSubmitButtons()
      return true
    }
  }

  async runAfterCartAdd() {
    if (this.type != 'upsell') {
      history.pushState('', '', `${this.urlProduct}`)
      document.documentElement.classList.remove('is-overflow-hidden')
      document.documentElement.setAttribute('nbr-open-panel', `0`)

      await renderAllSections(this.sectionsRender)
      document
        .querySelector('c-panel[product-was-add]')
        ?.setAttribute('product-was-add', 'true')
      if (this.btnSubmit) this.btnSubmit.removeAttribute('disabled')
      if (this.btnSubmitAlreadyInfos)
        this.btnSubmitAlreadyInfos.removeAttribute('disabled')
      this.sectionBtnSticky.forEach((el) => el.classList.remove('is-hide'))
      this.allPanel.forEach((el) => el.classList.remove('is-open'))
    } else {
      document.documentElement.setAttribute('nbr-open-panel', `1`)

      await renderAllSections(this.sectionsRender)
      this.restoreSubmitButtons()
    }
  }

  fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`,
      },
    }
  }
  disconnectedCallback() {}
}
if (!customElements.get('c-product-form')) {
  customElements.define('c-product-form', CProductForm)
}
